import { useState } from 'react';
import { AlertTriangle, ExternalLink, Loader2, MapPin, Search, Contact } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation } from '@/lib/api-hooks';
import { isDesktopApp } from '@/lib/desktopApp';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { buttonClass, errorMessage, lookupSourceLabel, truecallerResult, truecallerSearchUrl } from './format';
import type { NumberLookup } from './types';

function MainResult({ lookup, compact }: { lookup: NumberLookup; compact: boolean }) {
  if (lookup.status === 'queued' || lookup.status === 'pending') {
    return (
      <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
        <Loader2 className="w-3 h-3 animate-spin" /> Looking up who this is…
      </p>
    );
  }
  if (lookup.status === 'not_found') {
    return compact ? (
      <p className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
        <Search className="w-3 h-3" /> No public listing found
      </p>
    ) : null;
  }
  if (lookup.status === 'error') {
    return compact ? <p className="text-xs text-gray-400 dark:text-gray-500">Look-up failed; try again later</p> : null;
  }

  const source = lookupSourceLabel(lookup);
  return (
    <div className="space-y-0.5">
      <p className="flex flex-wrap items-center gap-x-1">
        {lookup.source === 'google' ? <MapPin className="w-3 h-3 shrink-0" /> : <Search className="w-3 h-3 shrink-0" />}
        <span>Possibly</span>
        <span className="font-semibold">{lookup.name}</span>
        <span className="text-gray-500 dark:text-gray-400">
          ·{' '}
          {lookup.sourceUrl ? (
            <a href={lookup.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 hover:underline">
              {source} <ExternalLink className="w-2.5 h-2.5" />
            </a>
          ) : (
            source
          )}
        </span>
      </p>
      {lookup.detail && <p className={`text-gray-500 dark:text-gray-400 ${compact ? 'truncate' : 'line-clamp-2'}`}>{lookup.detail}</p>}
      {!lookup.confirmed && (
        <p className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
          <AlertTriangle className="w-3 h-3" /> Could not open the page to confirm; check the source first
        </p>
      )}
    </div>
  );
}

function TruecallerResult({ result }: { result: NonNullable<NumberLookup['truecaller']> }) {
  if (result.status === 'pending') {
    return (
      <p className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
        <Loader2 className="w-3 h-3 animate-spin" /> Asking Truecaller…
      </p>
    );
  }
  return (
    <p className="flex flex-wrap items-center gap-x-1">
      <Contact className="w-3 h-3 shrink-0" />
      <span className="text-gray-500 dark:text-gray-400">Truecaller:</span>
      {result.status === 'found' ? (
        <>
          <span className="font-semibold">{result.name}</span>
          {result.company && result.company !== result.name && <span>({result.company})</span>}
          <span className="text-gray-500 dark:text-gray-400">· from people's address books, check before saving</span>
        </>
      ) : result.status === 'not_found' ? (
        <span className="text-gray-500 dark:text-gray-400">no name</span>
      ) : (
        <span className="text-gray-500 dark:text-gray-400">{result.error ?? 'failed'}</span>
      )}
      {result.spam && <span className="text-red-600 dark:text-red-400 font-medium">· reported as spam</span>}
    </p>
  );
}

/** A number's look-up: who it probably is, where that came from, or that it is still being looked up. */
export function LookupSuggestion({ lookup, compact = false }: { lookup: NumberLookup | null | undefined; compact?: boolean }) {
  const { data, hasFeature } = useCrm();
  if (!lookup) return null;
  // A number only searched on Truecaller waits in the normal queue; say nothing about it unless that look-up is on.
  const mainRuns = hasFeature('callerLookup') && Boolean(data?.capabilities.lookup);
  const showMain = lookup.status === 'found' || mainRuns;
  const truecaller = truecallerResult(lookup);
  const showBox = !compact && (lookup.status === 'found' || truecaller);
  const content = (
    <>
      {showMain && <MainResult lookup={lookup} compact={compact} />}
      {truecaller && <TruecallerResult result={truecaller} />}
    </>
  );
  if (compact) return <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5">{content}</div>;
  if (!showBox) return content;
  return <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 p-2 text-xs text-sky-900 dark:text-sky-100 space-y-1">{content}</div>;
}

/**
 * Feature `truecallerLookup`: "Open in Truecaller" opens the number on
 * truecaller.com, where staff are signed in. The desktop app has no way yet to open it in the
 * browser, so the link is left out there. For customers allowed the server search (beta, see
 * truecallerSearchFor), a second button searches from here.
 */
export function TruecallerButton({ number, lookup, className }: { number: string; lookup?: NumberLookup | null; className?: string }) {
  const { data, hasFeature } = useCrm();
  const request = useMutation(backendApi.crmLookup.requestTruecaller);
  const [busy, setBusy] = useState(false);
  const url = truecallerSearchUrl(number);
  if (!hasFeature('truecallerLookup') || !url) return null;
  const canSearch = Boolean(data?.capabilities.truecaller && data.canManage);
  const result = truecallerResult(lookup);
  const pending = result?.status === 'pending';
  return (
    <>
      {!isDesktopApp() && (
        <a href={url} target="_blank" rel="noopener noreferrer" className={className ?? buttonClass.small} title="Open this number on truecaller.com">
          <Contact className="w-3.5 h-3.5" /> Open in Truecaller
        </a>
      )}
      {canSearch && (
        <button
          type="button"
          disabled={busy || pending}
          onClick={async () => {
            setBusy(true);
            try {
              await request({ number });
            } catch (error) {
              toast.error(errorMessage(error));
            } finally {
              setBusy(false);
            }
          }}
          className={className ?? buttonClass.small}
          title="Search this number on Truecaller from the server (beta)"
        >
          <Contact className="w-3.5 h-3.5" /> {result && !pending ? 'Search Truecaller again' : 'Search Truecaller'}
        </button>
      )}
    </>
  );
}
